import * as React from "react";
import { Route, RouteProps } from "react-router-dom";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { saveNewToken } from "../state/ducks/auth/operations";
import { useAuth0 } from "./react-auth0-spa";
import { AppState } from "../state/store";

interface PrivateRouteProps extends RouteProps {
  component: any;
  path: string;
}

const mapStateToProps = (state: AppState) => {
  return {
    token: state.authReducer.token,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    SaveNewToken: async (token: string) =>
      (await saveNewToken(token))(dispatch),
  };
};

type Props = PrivateRouteProps &
  ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const RENEWAL_TIME_MS = 1800 * 1000; // Renew 30min before
export const PrivateRoute = (props: Props) => {
  const {
    loading,
    isAuthenticated,
    loginWithRedirect,
    getTokenSilently,
  } = useAuth0();
  const { component: Component, ...rest } = props;

  React.useEffect(() => {
    if (loading || isAuthenticated) {
      return;
    }
    const fn = async () => {
      window.sessionStorage.setItem("url", window.location.pathname);
      await loginWithRedirect({});
    };
    fn();
  }, [loading, isAuthenticated, loginWithRedirect, props.path]);

  const GetToken = async () => {
    let token = await getTokenSilently();
    props.SaveNewToken(token);
    scheduleTokenRenewal(token);
  };

  const render = () => {
    if (!isAuthenticated) return null;
    if (!props.token) {
      GetToken();
      return null;
    }

    return <Component {...props} />;
  };

  function scheduleTokenRenewal(token: string) {
    unscheduleRenewal();

    if (!isAuthenticated) {
      return;
    }
    let jwtToken = decodeToken(token);
    let expiresAt = jwtToken.exp;
    let expiresAtDate = new Date(expiresAt * 1000);

    if (expiresAt && expiresAtDate) {
      let timeToRenewal =
        expiresAtDate.getTime() - new Date().getTime() - RENEWAL_TIME_MS;
      if (timeToRenewal < 0) {
        timeToRenewal = 0;
      }

      renewalTimeout = setTimeout(() => {
        if (
          expiresAtDate.getTime() - new Date().getTime() - RENEWAL_TIME_MS <
          0
        ) {
          GetToken();
        }
      }, timeToRenewal);
    }
  }

  let renewalTimeout: NodeJS.Timeout | undefined;
  function unscheduleRenewal() {
    if (renewalTimeout) {
      clearTimeout(renewalTimeout);
      renewalTimeout = undefined;
    }
  }

  return <Route path={props.path} render={render} {...rest} />;
};

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute);

export function decodeToken(token: string | null) {
  if (token === null || token === "") {
    return { upn: "" };
  }
  const parts = token.split(".");
  if (parts.length !== 3) {
    throw new Error("JWT must have 3 parts");
  }
  const decoded = urlBase64Decode(parts[1]);
  if (!decoded) {
    throw new Error("Cannot decode the token");
  }
  return JSON.parse(decoded);
}
function urlBase64Decode(str: string) {
  let output = str.replace(/-/g, "+").replace(/_/g, "/");
  switch (output.length % 4) {
    case 0:
      break;
    case 2:
      output += "==";
      break;
    case 3:
      output += "=";
      break;
    default:
      throw new Error("Illegal base64url string!");
  }

  return decodeURIComponent((window as any).escape(window.atob(output)));
}
